<!-- src/components/student/StudentHomework.vue -->
<template>
	<div class="view-589">
		<div class="view-590">
			<!-- Заголовок блока + Фильтр -->
			<div class="header-row">
				<div class="text-890 poppins-bold-thunder-22px">Домашние задания</div>

				<!-- Фильтр по курсам -->
				<div class="course-filter">
					<label class="filter-label">Курс:</label>
					<select
						v-model="selectedCourse"
						class="course-select"
						@change="onCourseChange"
					>
						<option value="">Все курсы</option>
						<option
							v-for="course in availableCourses"
							:key="course"
							:value="course"
						>
							{{ course }}
						</option>
					</select>
				</div>
			</div>

			<!-- Заголовки столбцов -->
			<div class="columns-header">
				<div class="text-875 poppins-medium-thunder-20px">Задания</div>
				<div class="text-876 poppins-medium-thunder-20px">Дедлайн</div>
				<div class="text-874 poppins-medium-thunder-20px">Статус</div>
			</div>

			<!-- Загрузка -->
			<div v-if="loading" class="loading-state">
				<span>Загрузка заданий...</span>
			</div>

			<!-- Пустое состояние -->
			<div v-else-if="filteredHomework.length === 0" class="empty-state">
				<span v-if="selectedCourse">Нет заданий для выбранного курса</span>
				<span v-else>Нет домашних заданий</span>
			</div>

			<!-- Список ДЗ -->
			<div v-else class="homework-list">
				<!-- Если выбран конкретный курс - показываем без группировки -->
				<template v-if="selectedCourse">
					<div
						v-for="(hw, index) in flatFilteredHomework"
						:key="hw.id || index"
						class="homework-row"
					>
						<!-- Название -->
						<p class="homework-text poppins-normal-thunder-17px">
							{{ hw.name }}
						</p>

						<!-- Дедлайн -->
						<div class="homework-deadline poppins-normal-thunder-18px">
							{{ formatDeadline(hw) || '—' }}
						</div>

						<!-- Статус / Кнопка -->
						<div class="homework-status">
							<template v-if="hw.status === 'available'">
								<router-link
									:to="getHomeworkLink(hw)"
									class="status-btn status-btn--available"
								>
									<div class="btn-bg btn-bg--orange"></div>
									<span class="btn-text">Доступно</span>
								</router-link>
							</template>
							<template v-else>
								<div class="status-btn status-btn--locked">
									<div class="btn-bg btn-bg--gray"></div>
									<span class="btn-text">Закрыто</span>
								</div>
							</template>
						</div>
					</div>
				</template>

				<!-- Если "Все курсы" - показываем с группировкой -->
				<template v-else>
					<div
						v-for="(group, groupIndex) in filteredHomework"
						:key="group.courseName || groupIndex"
						class="course-group"
					>
						<!-- Заголовок курса -->
						<div class="course-header">
							<span class="course-icon">📚</span>
							<span class="course-name">{{ group.courseName }}</span>
							<span class="course-count">({{ group.items.length }})</span>
						</div>

						<!-- ДЗ курса -->
						<div
							v-for="(hw, index) in group.items"
							:key="hw.id || index"
							class="homework-row"
						>
							<!-- Название -->
							<p class="homework-text poppins-normal-thunder-17px">
								{{ hw.name }}
							</p>

							<!-- Дедлайн -->
							<div class="homework-deadline poppins-normal-thunder-18px">
								{{ formatDeadline(hw) || '—' }}
							</div>

							<!-- Статус / Кнопка -->
							<div class="homework-status">
								<template v-if="hw.status === 'available'">
									<router-link
										:to="getHomeworkLink(hw)"
										class="status-btn status-btn--available"
									>
										<div class="btn-bg btn-bg--orange"></div>
										<span class="btn-text">Доступно</span>
									</router-link>
								</template>
								<template v-else>
									<div class="status-btn status-btn--locked">
										<div class="btn-bg btn-bg--gray"></div>
										<span class="btn-text">Закрыто</span>
									</div>
								</template>
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import { formatDateTime } from '@/utils/dateTime'
export default {
	name: 'StudentHomework',

	props: {
		/**
		 * Массив групп домашних заданий
		 * Формат: [{ courseName: 'Курс 1', items: [...] }, ...]
		 */
		homework: {
			type: Array,
			default: () => [],
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			selectedCourse: '',
		}
	},

	computed: {
		/**
		 * Список уникальных курсов для фильтра
		 */
		availableCourses() {
			if (!this.homework || !Array.isArray(this.homework)) {
				return []
			}

			return this.homework
				.filter(group => {
					if (
						!group ||
						!group.courseName ||
						this.isGroupPaused(group) ||
						this.isGroupCompleted(group)
					)
						return false
					return this.getVisibleItems(group).length > 0
				})
				.map(group => group.courseName)
				.sort((a, b) => a.localeCompare(b))
		},

		/**
		 * Отфильтрованные ДЗ (группы)
		 */
		filteredHomework() {
			if (!this.homework || !Array.isArray(this.homework)) {
				return []
			}

			if (!this.selectedCourse) {
				//Проверка на ошибки
				return this.homework
					.map(group => {
						if (!group) return null
						const items = this.getVisibleItems(group)
						if (!items.length) return null
						return { ...group, items }
					})
					.filter(
						group =>
							group &&
							!this.isGroupPaused(group) &&
							!this.isGroupCompleted(group),
					)
			}

			// Добавить в избранное курс
			return this.homework
				.map(group => {
					if (!group || group.courseName !== this.selectedCourse) return null
					const items = this.getVisibleItems(group)
					if (!items.length) return null
					return { ...group, items }
				})
				.filter(
					group =>
						group &&
						!this.isGroupPaused(group) &&
						!this.isGroupCompleted(group),
				)
		},

		/**
		 * Плоский список ДЗ для выбранного курса
		 */
		flatFilteredHomework() {
			if (!this.selectedCourse) {
				return []
			}

			const group = this.homework.find(
				g => g.courseName === this.selectedCourse
			)
			if (!group || this.isGroupPaused(group) || this.isGroupCompleted(group)) {
				return []
			}
			return this.getVisibleItems(group)
		},
	},

	methods: {
		isHomeworkHidden(hw) {
			if (!hw) return false
			if (hw.isHidden === true) return true
			if (hw.hidden === true) return true
			if (hw.is_hidden === true || hw.is_draft === true) return true
			const rawStatus = String(hw.status || '').trim().toLowerCase()
			return ['hidden', 'draft'].includes(rawStatus)
		},

		getVisibleItems(group) {
			if (!group || !Array.isArray(group.items)) return []
			return group.items.filter(item => !this.isHomeworkHidden(item))
		},

		onCourseChange() {
			this.$emit('course-filter-change', this.selectedCourse)
		},

		isPausedStatus(value) {
			if (!value && value !== 0) return false
			const normalized = String(value).trim().toLowerCase()
			return [
				'paused',
				'on_hold',
				'on-hold',
				'hold',
				'stopped',
				'suspended',
			].includes(normalized)
		},

		isCompletedStatus(value) {
			if (!value && value !== 0) return false
			const normalized = String(value).trim().toLowerCase()
			return ['completed', 'done', 'finished', 'passed'].includes(normalized)
		},

		toNumber(value) {
			const parsed = Number(value)
			return Number.isFinite(parsed) ? parsed : null
		},

		isCoursePaused(entity) {
			if (!entity) return false
			if (entity.isPaused === true || entity.paused === true) return true
			if (entity.coursePaused === true || entity.course_paused === true) return true

			const status =
				entity.enrollmentStatus ??
				entity.enrollment_status ??
				entity.courseStatus ??
				entity.course_status ??
				null
			return this.isPausedStatus(status)
		},

		isCourseCompleted(entity) {
			if (!entity) return false
			const looksLikeHomeworkEntity =
				entity.moduleId !== undefined ||
				entity.module_id !== undefined ||
				entity.homeworkId !== undefined ||
				entity.homework_id !== undefined ||
				entity.submissionStatus !== undefined ||
				entity.submission_status !== undefined ||
				entity.submissionState !== undefined ||
				entity.submission_state !== undefined
			if (looksLikeHomeworkEntity) {
				const courseStatus =
					entity.enrollmentStatus ??
					entity.enrollment_status ??
					entity.courseStatus ??
					entity.course_status ??
					null
				if (this.isCompletedStatus(courseStatus)) return true
				if (entity.course_is_completed === true || entity.courseIsCompleted === true)
					return true
				if (entity.course_completed === true) return true
				if (entity.course_completed_at || entity.courseCompletedAt) return true
				return false
			}

			if (entity.is_completed === true || entity.isCompleted === true) return true
			if (entity.completed === true) return true
			if (entity.completed_at || entity.completedAt) return true

			const status =
				entity.enrollmentStatus ??
				entity.enrollment_status ??
				entity.courseStatus ??
				entity.course_status ??
				entity.status ??
				entity.state ??
				null

			if (this.isCompletedStatus(status)) return true

			const progress = this.toNumber(
				entity.progressPercent ??
					entity.progress_percent ??
					entity.progress ??
					null,
			)
			const currentLesson = this.toNumber(
				entity.currentLesson ??
					entity.current_lesson ??
					entity.current_lesson_number ??
					null,
			)
			const totalLessons = this.toNumber(
				entity.totalLessons ??
					entity.lessons_total ??
					entity.total_lessons ??
					null,
			)

			if (
				totalLessons &&
				currentLesson !== null &&
				currentLesson >= totalLessons
			) {
				return true
			}
			if (progress !== null && progress >= 100) return true
			return false
		},

		isGroupPaused(group) {
			if (!group) return false
			if (this.isCoursePaused(group)) return true
			const sampleItem = Array.isArray(group.items)
				? group.items.find(item => item)
				: null
			return this.isCoursePaused(sampleItem)
		},

		isGroupCompleted(group) {
			if (!group) return false
			if (this.isCourseCompleted(group)) return true
			const sampleItem = Array.isArray(group.items)
				? group.items.find(item => item)
				: null
			return this.isCourseCompleted(sampleItem)
		},

		formatDeadline(hw) {
			if (!hw) return ''
			const dateValue =
				hw.deadline ||
				hw.due_date ||
				hw.dueDate ||
				hw.due_at ||
				hw.dueAt ||
				hw.deadline_at ||
				hw.deadlineAt ||
				null
			const timeValue =
				hw.due_time || hw.dueTime || hw.deadline_time || hw.deadlineTime || null
			return formatDateTime(dateValue, timeValue)
		},

		getHomeworkLink(hw) {
			// Если есть moduleId — переходим на страницу модуля с открытым ДЗ
			if (hw?.id && hw?.moduleId) {
				return {
					name: 'VideoLessons',
					query: {
						moduleId: hw.moduleId,
						homeworkId: hw.id,
						section: 'homework',
					},
				}
			}
			// Fallback на старую страницу
			if (hw?.id) {
				return { name: 'HomeworkPage', query: { id: hw.id } }
			}
			return '/homework-page'
		},
	},
}
</script>

<style scoped>
.view-589 {
	background-color: var(--white);
	border-radius: 20px;
	display: flex;
	flex-direction: column;
	min-height: 400px;
	max-height: 650px;
	width: 819px;
	overflow: hidden;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.view-590 {
	display: flex;
	flex-direction: column;
	min-height: 349px;
	max-height: 600px;
	margin-left: 35px;
	margin-top: 25px;
	margin-right: 20px;
	padding-bottom: 20px;
}

/* Заголовок + Фильтр */
.header-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
	flex-shrink: 0;
}

.text-890 {
	letter-spacing: 1.54px;
	line-height: normal;
}

/* Фильтр курсов */
.course-filter {
	display: flex;
	align-items: center;
	gap: 8px;
}

.filter-label {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	color: #666;
}

.course-select {
	padding: 8px 12px;
	border: 2px solid #e0e0e0;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 14px;
	color: #333;
	background-color: #fff;
	cursor: pointer;
	min-width: 180px;
	max-width: 250px;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.course-select:hover {
	border-color: #ff9800;
}

.course-select:focus {
	outline: none;
	border-color: #ff9800;
	box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.1);
}

/* Заголовки столбцов */
.columns-header {
	display: flex;
	align-items: center;
	padding: 10px 0;
	border-bottom: 1px solid #e0e0e0;
	margin-bottom: 10px;
	flex-shrink: 0;
}

.text-875 {
	width: 322px;
	letter-spacing: 0;
	line-height: normal;
}

.text-876 {
	width: 130px;
	margin-left: 70px;
	letter-spacing: 0;
	line-height: normal;
}

.text-874 {
	margin-left: auto;
	letter-spacing: 0;
	line-height: normal;
}

/* Список ДЗ */
.homework-list {
	overflow-y: auto;
	overflow-x: hidden;
	flex: 1;
}

/* Стилизация скроллбара */
.homework-list::-webkit-scrollbar {
	width: 6px;
}

.homework-list::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 10px;
}

.homework-list::-webkit-scrollbar-thumb {
	background: #c1c1c1;
	border-radius: 10px;
}

.homework-list::-webkit-scrollbar-thumb:hover {
	background: #a1a1a1;
}

/* Группа курса */
.course-group {
	margin-bottom: 15px;
}

.course-header {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 12px;
	background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
	border-radius: 8px;
	margin-bottom: 8px;
	border-left: 4px solid #ff9800;
}

.course-icon {
	font-size: 18px;
}

.course-name {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 16px;
	font-weight: 600;
	color: #2c3e50;
}

.course-count {
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 13px;
	color: #888;
}

/* Строка ДЗ */
.homework-row {
	display: flex;
	align-items: center;
	padding: 12px 8px;
	border-radius: 8px;
	transition: background-color 0.2s ease;
}

.homework-row:hover {
	background-color: #fff8e1;
}

.homework-text {
	width: 322px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.4;
}

.homework-deadline {
	width: 150px;
	margin-left: 50px;
	letter-spacing: 0;
	line-height: normal;
	color: #666;
}

.homework-status {
	margin-left: auto;
	height: 40px;
	width: 150px;
}

.status-btn {
	position: relative;
	width: 150px;
	height: 40px;
	display: block;
	text-decoration: none;
}

.btn-bg {
	border-radius: 10px;
	height: 40px;
	left: 0;
	position: absolute;
	top: 0;
	width: 150px;
	transition: box-shadow 0.2s ease;
}

.btn-bg--orange {
	background-color: #ff9800;
	cursor: pointer;
}

.btn-bg--gray {
	background-color: #636363;
	cursor: not-allowed;
}

.btn-text {
	left: 29px;
	letter-spacing: 0;
	line-height: normal;
	position: absolute;
	top: 8px;
	width: 92px;
	text-align: center;
	color: white;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 18px;
	font-weight: 600;
}

.status-btn--available:hover .btn-bg--orange {
	box-shadow: 0 4px 8px rgba(255, 152, 0, 0.3);
}

.status-btn--available {
	transition: transform 0.2s ease;
}

.status-btn--available:hover {
	transform: translateY(-2px);
}

.status-btn--available:active {
	transform: translateY(0);
}

.loading-state,
.empty-state {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 150px;
	color: #666;
	font-size: 16px;
	font-family: 'Poppins', Helvetica, sans-serif;
}
</style>
